import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';

const PaymentsSection = ({ trainerId }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0 });
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (trainerId) fetchPayments();
  }, [trainerId]);

  const fetchPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('trainer_payments_20250218')
        .select(`
          *,
          training_clients(dog_name, owner_name),
          training_plans(plan_name)
        `)
        .eq('trainer_id', trainerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPayments(data || []);

      const total = data?.filter(p => p.status === 'paid').reduce((acc, curr) => acc + Number(curr.amount), 0) || 0;
      const pending = data?.filter(p => p.status === 'pending').reduce((acc, curr) => acc + Number(curr.amount), 0) || 0;
      setStats({ total, pending });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (paymentId, planId) => {
    setUpdatingId(paymentId);
    try {
      // 1. Update payment record
      await supabase.from('trainer_payments_20250218')
        .update({ status: 'paid', payment_date: new Date().toISOString() })
        .eq('id', paymentId);

      // 2. Unlock the training plan
      await supabase.from('training_plans')
        .update({ is_paid: true })
        .eq('id', planId);

      fetchPayments();
    } catch (err) {
      alert('Failed to update payment status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <div className="p-8 animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-2xl border" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white shadow-xl shadow-indigo-100 relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-indigo-100 text-sm font-black uppercase tracking-widest mb-1">Total Revenue</p>
            <h3 className="text-4xl font-black">${stats.total.toLocaleString()}</h3>
          </div>
          <SafeIcon name="Award" className="absolute -right-4 -bottom-4 w-32 h-32 text-white/10 rotate-12" />
        </div>
        <div className="bg-white rounded-[2.5rem] p-8 border-2 border-gray-100 shadow-sm relative overflow-hidden text-gray-900">
          <p className="text-gray-400 text-sm font-black uppercase tracking-widest mb-1">Pending Invoices</p>
          <h3 className="text-4xl font-black text-indigo-600">${stats.pending.toLocaleString()}</h3>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b bg-gray-50/50 flex items-center justify-between">
          <h3 className="font-black text-gray-900">Recent Transactions</h3>
          <button onClick={fetchPayments} className="p-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all">
            <SafeIcon name="Search" className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b">
                <th className="px-6 py-4">Client / Dog</th>
                <th className="px-6 py-4">Plan Name</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {payments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-gray-900 text-sm">{p.training_clients?.dog_name}</p>
                    <p className="text-[10px] text-gray-400">{p.training_clients?.owner_name}</p>
                  </td>
                  <td className="px-6 py-4 text-xs font-medium text-gray-600">{p.training_plans?.plan_name}</td>
                  <td className="px-6 py-4 font-black text-sm text-gray-900">${p.amount}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {p.status === 'pending' && (
                      <button 
                        onClick={() => markAsPaid(p.id, p.plan_id)} 
                        disabled={updatingId === p.id}
                        className="text-[10px] font-black text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-all flex items-center gap-2"
                      >
                        {updatingId === p.id ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon name="Check" />}
                        Confirm Payment
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.length === 0 && (
            <div className="py-20 text-center text-gray-400 italic font-medium">No transactions found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsSection;